import React, { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  BreadcrumbItem,
  BreadcrumbProps,
  CmhcIconSize,
  Tooltip,
  TooltipPosition
} from '@cmhc-schl/hearth-ui';
import { ClickAwayListener, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import useEnterOrSpaceKey from 'Components/Hooks/UseEnterOrSpaceKey';
import i18n from 'i18next';
import { NavLink, useHistory } from 'react-router-dom';
import styles from './styles.module.scss';

const BreadcrumbItemAdapter: FC<{ item: BreadcrumbItem; isLastItem: boolean }> = ({
  item,
  isLastItem
}) => (
  <div
    className={`${styles.breadcrumbItem} ${item.isCurrentPage ? styles.currentPage : ''}`}
    aria-label={item.label}
    data-testid="breadcrumb-item"
  >
    {!isLastItem ? (
      <NavLink
        className={styles.breadcrumbLink}
        to={item.href ?? '#'}
        data-testid="menuLink"
        aria-label={item.label}
      >
        {item.label}
      </NavLink>
    ) : (
      <div className={styles.currentPageLabel}>{item.label}</div>
    )}
  </div>
);

const BreadcrumbDropdownAdapter: FC<{ items: BreadcrumbItem[] }> = memo(({ items }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const history = useHistory();

  const handleToggle = useCallback(() => {
    setDropdownVisible((prev) => !prev);
  }, []);

  const handleMenuItemClick = (item: BreadcrumbItem) => {
    setDropdownVisible(false);
    history.push(item.href ?? '/#');
  };

  const ellipsisStyles = useMemo(
    () => `${styles.overflowEllipses} ${isActive ? styles.active : ''}`,
    [isActive]
  );

  return (
    <Tooltip
      label="More pages"
      position={TooltipPosition.BOTTOM_RIGHT}
      iconSize={CmhcIconSize.X_LARGE}
      customStyles={{ top: '70px', left: '3px' }}
    >
      <ClickAwayListener onClickAway={() => setDropdownVisible(false)}>
        <div style={{ padding: 0, margin: 0 }}>
          <span
            role="button"
            className={ellipsisStyles}
            tabIndex={0}
            ref={anchorRef}
            data-testid="breadcrumb-ellipses"
            aria-label={i18n.t('BreadcrumbEllipsisLabel') ?? 'See more options'}
            onClick={handleToggle}
            onKeyDown={useEnterOrSpaceKey(handleToggle)}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            onBlur={() => setIsActive(false)}
          />
          <Popper
            open={dropdownVisible}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            className={styles.dropdownMenu}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [152, 7]
                }
              }
            ]}
            data-testid="breadcrumb-menu-list"
          >
            <Paper className={styles.menuFrame}>
              <MenuList className={styles.menuList}>
                {items.map((item, index) => (
                  <MenuItem
                    className={styles.menuItem}
                    key={index}
                    data-testid="breadcrumb-menu-item"
                    onClick={() => handleMenuItemClick(item)}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    </Tooltip>
  );
});
BreadcrumbDropdownAdapter.displayName = 'BreadcrumbDropdownAdapter';

const BreadcrumbV5Adapter = memo(({ items = [], condensed = false }: BreadcrumbProps) => {
  const breadcrumbItems = Array.isArray(items) ? items : [];
  if (breadcrumbItems.length === 0) return null;

  const lastIndex = breadcrumbItems.length - 1;
  const shouldCondense = condensed && breadcrumbItems.length > 5;
  const dropdownItems = shouldCondense ? breadcrumbItems.slice(2, lastIndex - 1) : [];

  return (
    <nav
      className={`${styles.breadcrumb} ${condensed ? styles.condensed : ''}`}
      aria-label="breadcrumb"
    >
      {!shouldCondense ? (
        breadcrumbItems.map((item, index) => (
          <BreadcrumbItemAdapter key={item.label} item={item} isLastItem={index === lastIndex} />
        ))
      ) : (
        <>
          <BreadcrumbItemAdapter
            key={breadcrumbItems[0].label}
            item={breadcrumbItems[0]}
            isLastItem={false}
          />
          <BreadcrumbItemAdapter
            key={breadcrumbItems[1].label}
            item={breadcrumbItems[1]}
            isLastItem={false}
          />
          {dropdownItems.length > 0 && <BreadcrumbDropdownAdapter items={dropdownItems} />}
          <BreadcrumbItemAdapter
            key={breadcrumbItems[lastIndex - 1].label}
            item={breadcrumbItems[lastIndex - 1]}
            isLastItem={false}
          />
          <BreadcrumbItemAdapter
            key={breadcrumbItems[lastIndex].label}
            item={breadcrumbItems[lastIndex]}
            isLastItem={true}
          />
        </>
      )}
    </nav>
  );
});

BreadcrumbV5Adapter.displayName = 'BreadcrumbV5Adapter';

export default BreadcrumbV5Adapter;
